/**
 * Window events for app-wide dialogs that load after hydration (_app.tsx), held until they listen.
 *
 * The SEC filing viewer listens for `ortex:open-sec-doc`, and the news and call dialogs for
 * `ortex:open-modal-link` with `detail.kind` "news" or "call". They used to be in the shell, so their
 * listeners existed from hydration; now their code loads just after it. A click in between would
 * dispatch an event nobody hears and be lost, so each event is recorded until the host it is for has
 * mounted, then dispatched again.
 *
 * Events are held per host: an event type plus, where several hosts share the type, the `kind` in its
 * detail. A host that is already listening has handled the event live and ignores another kind's
 * replay, so no host handles an event twice.
 */
type Pending = { key: string; type: string; detail: unknown };

const pending: Pending[] = [];
const waiting = new Set<string>();

const keyOf = (type: string, detail: unknown): string => {
    const kind = (detail as { kind?: unknown } | null)?.kind;
    return typeof kind === "string" ? `${type}|${kind}` : type;
};

const record = (event: Event) => {
    const detail = (event as CustomEvent).detail;
    const key = keyOf(event.type, detail);
    if (waiting.has(key)) pending.push({ key, type: event.type, detail });
};

const typeOf = (key: string) => key.split("|")[0];

/** Starts recording events for each host key ("type", or "type|kind") until hostListening(key). */
export const holdEarlyEvents = (keys: string[]) => {
    if (typeof window === "undefined") return;
    for (const key of keys) {
        if (waiting.has(key)) continue;
        if (![...waiting].some((k) => typeOf(k) === typeOf(key))) window.addEventListener(typeOf(key), record);
        waiting.add(key);
    }
};

/** The host for `key` has mounted and listens: the events recorded for it are dispatched again. */
export const hostListening = (key: string) => {
    if (!waiting.delete(key)) return;
    const type = typeOf(key);
    if (![...waiting].some((k) => typeOf(k) === type)) window.removeEventListener(type, record);
    const replay = pending.filter((event) => event.key === key);
    for (let i = pending.length - 1; i >= 0; i--) if (pending[i].key === key) pending.splice(i, 1);
    replay.forEach(({ detail }) => window.dispatchEvent(new CustomEvent(type, { detail })));
};
